import { isMobileWebView } from './mobileDetection';

export type DevicePlatform = 'macos' | 'windows' | 'linux' | 'ios' | 'android' | 'unknown';
export type DeviceSurface = 'desktop_browser' | 'mobile_browser' | 'mobile_app_webview' | 'unknown';
export type DeviceFormFactor = 'desktop' | 'mobile' | 'tablet' | 'unknown';

export interface AccessDeviceFlags {
  desktop?: boolean;
  mobileBrowser?: boolean;
  mobileApp?: boolean;
  ios?: boolean;
  android?: boolean;
  macos?: boolean;
  windows?: boolean;
  linux?: boolean;
}

export interface AccessDeviceInfo {
  platform: DevicePlatform;
  surface: DeviceSurface;
  formFactor: DeviceFormFactor;
  userAgent: string;
  appVersion?: string;
  flags: AccessDeviceFlags;
}

const getMobilePlatformHint = (): DevicePlatform => {
  // Prefer explicit hints injected by the native WebView
  const platformHint = (window as any)._jampackdMobilePlatform as string | undefined;
  if (platformHint === 'ios' || platformHint === 'android') {
    return platformHint;
  }

  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';

  return 'unknown';
};

const detectPlatform = (): DevicePlatform => {
  const ua = navigator.userAgent.toLowerCase();

  // Check explicit mobile platforms first to avoid "like Mac OS X" on iOS Safari
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (ua.includes('android')) return 'android';

  if (ua.includes('macintosh') || ua.includes('mac os x')) return 'macos';
  if (ua.includes('windows')) return 'windows';
  if (ua.includes('linux')) return 'linux';

  // If we're in the mobile app WebView but the UA is spoofed, use injected hint
  if (isMobileWebView()) {
    return getMobilePlatformHint();
  }

  return 'unknown';
};

const detectFormFactor = (platform: DevicePlatform): DeviceFormFactor => {
  const ua = navigator.userAgent.toLowerCase();
  const isTablet = /ipad|tablet/.test(ua);
  const isMobile = /iphone|ipod|android/.test(ua);

  if (isTablet) return 'tablet';
  if (isMobile) return 'mobile';
  if (platform === 'macos' || platform === 'windows' || platform === 'linux') return 'desktop';

  return 'unknown';
};

const detectSurface = (platform: DevicePlatform): DeviceSurface => {
  if (isMobileWebView()) return 'mobile_app_webview';

  // If we can confidently detect desktop platforms, prefer that before UA heuristics
  if (platform === 'macos' || platform === 'windows' || platform === 'linux') {
    return 'desktop_browser';
  }

  // Known mobile platforms
  if (platform === 'ios' || platform === 'android') {
    return 'mobile_browser';
  }

  const ua = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipod|android|mobile/.test(ua);
  if (isMobile) return 'mobile_browser';

  return 'unknown';
};

const getAppVersion = (): string | undefined => {
  const injected = (window as any)._jampackdMobileAppVersion as string | undefined;
  return injected || undefined;
};

/**
 * Returns a normalized view of the current device for analytics.
 */
export const getAccessDeviceInfo = (): AccessDeviceInfo => {
  const platform = detectPlatform();
  const surface = detectSurface(platform);
  const formFactor = detectFormFactor(platform);
  const userAgent = navigator.userAgent;
  const appVersion = getAppVersion();

  const flags: AccessDeviceFlags = {
    desktop: surface === 'desktop_browser',
    mobileBrowser: surface === 'mobile_browser',
    mobileApp: surface === 'mobile_app_webview',
    ios: platform === 'ios',
    android: platform === 'android',
    macos: platform === 'macos',
    windows: platform === 'windows',
    linux: platform === 'linux'
  };

  return {
    platform,
    surface,
    formFactor,
    userAgent,
    appVersion,
    flags
  };
};
